import { Suspense } from 'react'

import { Loader2 } from 'lucide-react'
import type { ComponentProps, ComponentType, LazyExoticComponent } from 'react'

type AstaLoaderProps = {
	variant: 'circular' | 'linear'
	height?: string
	color?: string
}

const AstaLoader = ({
	variant,
	height,
	color,
}: AstaLoaderProps): JSX.Element =>
	String(variant).toLowerCase() === String('circular').toLowerCase() ? (
		<div
			className="flex items-center justify-center w-full"
			style={{ height: height || '100%' }}
			role="status"
			aria-busy="true"
		>
			<Loader2
				className={`w-10 h-10 animate-spin ${
					color
						? String(color).toLowerCase() === 'white'
							? 'text-white'
							: String(color).toLowerCase() === 'blue-500'
								? 'text-blue-500'
								: `text-${color}`
						: 'text-gray-900 dark:text-gray-100'
				}`}
			/>
		</div>
	) : (
		<div
			className="fixed top-0 left-0 w-full z-[2001]"
			role="status"
			aria-busy="true"
		>
			<div className="h-1 w-full bg-gray-200 overflow-hidden">
				<div
					className={`h-full ${color ? `bg-${color}` : 'bg-blue-600'} animate-linear-progress`}
				/>
			</div>
		</div>
	)

/**
 * Wraps a lazy component in a {@link Suspense} boundary with {@link AstaLoader}
 * as a fallback
 *
 * @example
 * ```ts
 * import React, { lazy } from 'react'
 *
 * const LazyComponent = lazy(() => import('path/to/component'))
 * const SuspendedComponent = WithLoaderSuspense(LazyComponent)
 * ```
 *
 * @param Component The lazy component
 * @returns the wrapped component
 */
const WithLoaderSuspense = <C extends ComponentType<any>>(
	Component: LazyExoticComponent<C>
) => {
	const Suspended = (props: ComponentProps<C>) => (
		<Suspense fallback={<AstaLoader variant="linear" />}>
			<Component {...props} />
		</Suspense>
	)
	Suspended.displayName = `WithLoaderSuspense(${
		(Component as LazyExoticComponent<C> & { displayName?: string })
			.displayName || Component.name
	})`
	return Suspended
}

export default AstaLoader
export { WithLoaderSuspense }
